import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, rem, Stack, Text, TextProps } from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { UserAnnouncements } from '../types/announcements';
import { AnnouncementTrackingEvent } from '../types/events';
import { EditAnnouncementButton } from './edit-announcement-button';

export interface ManageAnnouncementsModalProps {
	isOpen: boolean;
	onClose: VoidFunction;
	userAnnouncements?: UserAnnouncements;
}

export const ManageAnnouncementsModal: FC<ManageAnnouncementsModalProps> = ({ isOpen, onClose, userAnnouncements }) => {
	const { t } = useTranslation('announcements');
	const { track } = useAnalytics();
	const noUserAnnouncements = !userAnnouncements || (userAnnouncements.scheduled.length === 0 && userAnnouncements.posted.length === 0);

	useEffect(() => {
		// track only once when the modal is opened
		if (noUserAnnouncements || !isOpen) return;
		track(AnnouncementTrackingEvent.ManageAnnouncementsModalViewed);
	}, [isOpen, noUserAnnouncements, track]);

	if (noUserAnnouncements) return null;

	return (
		<Modal
			autoFocus={false}
			isOpen={isOpen}
			onClose={() => {
				track(AnnouncementTrackingEvent.ManageAnnouncementsModalClosed);
				onClose();
			}}
			scrollBehavior="inside"
			size="lg"
		>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader borderBottomWidth={rem(1)} borderColor="gray.100" borderStyle="solid" color="gray.900">
					{t('modal.manage_header')}
				</ModalHeader>
				<ModalCloseButton data-testid="close-button" size="lg" borderRadius="full" />

				<ModalBody p={5}>
					<Stack spacing={5}>
						{userAnnouncements.scheduled.length > 0 && (
							<Box>
								<HeaderText>{t('modal.scheduled')}</HeaderText>
								{userAnnouncements.scheduled.map((announcement) => (
									<EditAnnouncementButton key={announcement.id} announcement={announcement} type="scheduled" />
								))}
							</Box>
						)}
						{userAnnouncements.posted.length > 0 && (
							<Box>
								<HeaderText>{t('modal.posted')}</HeaderText>
								{userAnnouncements.posted.map((announcement) => (
									<EditAnnouncementButton key={announcement.id} announcement={announcement} type="posted" />
								))}
							</Box>
						)}
					</Stack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

const HeaderText = (props: TextProps) => {
	return <Text color="text.softest" fontSize={100} fontWeight="semibold" lineHeight={5} {...props} />;
};
